import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import ImageResize from "tiptap-extension-resize-image";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import FontSize from "tiptap-extension-font-size";
import LineHeight from "./LineHeightExtension";
import { generateSignedUrl } from "../../utils/getPreSignedURL";
import "./RichTextEditor.css";
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  TextField
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Link as LinkIcon,
  Image as ImageIcon,
  FormatClear,
  Title,
  FormatLineSpacing,
  Add,
  Remove
} from "@mui/icons-material";

// Link Dialog Component
const LinkDialog = ({ open, onClose, onSubmit, initialUrl = "" }) => {
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    setUrl(initialUrl);
  }, [initialUrl, open]);

  const handleSubmit = () => {
    onSubmit(url);
    setUrl("");
  };

  const handleRemove = () => {
    onSubmit("");
    setUrl("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Link</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="URL"
          type="url"
          fullWidth
          variant="outlined"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        {initialUrl && (
          <Button onClick={handleRemove} color="error">
            Remove Link
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {initialUrl ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Image Dialog Component
const ImageDialog = ({ open, onClose, onSubmit }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleSubmit = () => {
    if (selectedFile) {
      onSubmit(selectedFile, "file");
      setSelectedFile(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Image</DialogTitle>
      <DialogContent>
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            fullWidth
            onClick={() => fileInputRef.current?.click()}
            sx={{ mt: 1, mb: 2 }}
          >
            Choose Image File
          </Button>
          {selectedFile && (
            <Box
              sx={{
                p: 2,
                border: "1px solid #ccc",
                borderRadius: 1,
                backgroundColor: "#f5f5f5"
              }}
            >
              <strong>Selected:</strong> {selectedFile.name}
              <br />
              <small>Size: {(selectedFile.size / 1024).toFixed(2)} KB</small>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!selectedFile}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MenuBar = ({ editor, onOpenLinkDialog, onOpenImageDialog }) => {
  const [lineSpacingAnchor, setLineSpacingAnchor] = useState(null);

  if (!editor) {
    return null;
  }

  const handleLineSpacingClick = (event) => {
    setLineSpacingAnchor(event.currentTarget);
  };

  const handleLineSpacingClose = () => {
    setLineSpacingAnchor(null);
  };

  const setLineHeight = (height) => {
    if (height === "default") {
      editor.chain().focus().unsetLineHeight().run();
    } else {
      editor.chain().focus().setLineHeight(height).run();
    }
    handleLineSpacingClose();
  };

  const getCurrentFontSize = () => {
    const fontSize = editor.getAttributes('textStyle').fontSize;
    if (fontSize) {
      return parseInt(fontSize);
    }
    return 14; // Default font size
  };

  const increaseFontSize = () => {
    const currentSize = getCurrentFontSize();
    const newSize = Math.min(currentSize + 2, 48); // Max 48px
    editor.chain().focus().setFontSize(`${newSize}px`).run();
  };

  const decreaseFontSize = () => {
    const currentSize = getCurrentFontSize();
    const newSize = Math.max(currentSize - 2, 8); // Min 8px
    editor.chain().focus().setFontSize(`${newSize}px`).run();
  };

  return (
    <Box className="tiptap-toolbar">
      <Tooltip title="Heading 1">
        <IconButton
          size="small"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className={
            editor.isActive("heading", { level: 1 }) ? "is-active" : ""
          }
        >
          <Title fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Heading 2">
        <IconButton
          size="small"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={
            editor.isActive("heading", { level: 2 }) ? "is-active" : ""
          }
        >
          <Title fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <span className="toolbar-divider" />

      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "is-active" : ""}
        >
          <FormatBold fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "is-active" : ""}
        >
          <FormatItalic fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Underline">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive("underline") ? "is-active" : ""}
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Strike">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive("strike") ? "is-active" : ""}
        >
          <FormatStrikethrough fontSize="small" />
        </IconButton>
      </Tooltip>

      <span className="toolbar-divider" />

      <Tooltip title="Bullet List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "is-active" : ""}
        >
          <FormatListBulleted fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Ordered List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "is-active" : ""}
        >
          <FormatListNumbered fontSize="small" />
        </IconButton>
      </Tooltip>

      <span className="toolbar-divider" />

      <Tooltip title="Align Left">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={editor.isActive({ textAlign: "left" }) ? "is-active" : ""}
        >
          <FormatAlignLeft fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Align Center">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={
            editor.isActive({ textAlign: "center" }) ? "is-active" : ""
          }
        >
          <FormatAlignCenter fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Align Right">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={editor.isActive({ textAlign: "right" }) ? "is-active" : ""}
        >
          <FormatAlignRight fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Justify">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          className={
            editor.isActive({ textAlign: "justify" }) ? "is-active" : ""
          }
        >
          <FormatAlignJustify fontSize="small" />
        </IconButton>
      </Tooltip>

      <span className="toolbar-divider" />

      <Tooltip title="Decrease Font Size">
        <IconButton size="small" onClick={decreaseFontSize}>
          <Remove fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Increase Font Size">
        <IconButton size="small" onClick={increaseFontSize}>
          <Add fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Line Spacing">
        <IconButton size="small" onClick={handleLineSpacingClick}>
          <FormatLineSpacing fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={lineSpacingAnchor}
        open={Boolean(lineSpacingAnchor)}
        onClose={handleLineSpacingClose}
      >
        <MenuItem onClick={() => setLineHeight("1")}>
          <ListItemText>1.0</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setLineHeight("1.15")}>
          <ListItemText>1.15</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setLineHeight("1.5")}>
          <ListItemText>1.5</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setLineHeight("2")}>
          <ListItemText>2.0</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setLineHeight("2.5")}>
          <ListItemText>2.5</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setLineHeight("3")}>
          <ListItemText>3.0</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setLineHeight("default")}>
          <ListItemText>Default</ListItemText>
        </MenuItem>
      </Menu>

      <span className="toolbar-divider" />

      <Tooltip title="Add Link">
        <IconButton
          size="small"
          onClick={onOpenLinkDialog}
          className={editor.isActive("link") ? "is-active" : ""}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Add Image">
        <IconButton size="small" onClick={onOpenImageDialog}>
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <span className="toolbar-divider" />

      <Tooltip title="Clear Formatting">
        <IconButton
          size="small"
          onClick={() =>
            editor.chain().focus().clearNodes().unsetAllMarks().run()
          }
        >
          <FormatClear fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const RichTextEditor = forwardRef(
  ({ value, setValue, isLargeEditorNeeded = false }, ref) => {
    const editorRef = useRef(null);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [currentLinkUrl, setCurrentLinkUrl] = useState("");
    const isUpdatingRef = useRef(false);
    const hasLoadedInitialContent = useRef(false);

    const convertSignedUrlsToFilenames = useCallback((html) => {
      if (!html) return "";

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));

      images.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("http")) {
          // Extract filename from signed URL
          const urlParts = src.split("?")[0];
          const fileName = urlParts.split("/").pop();
          img.setAttribute("src", fileName);
        }
      });

      return doc.body.innerHTML;
    }, []);

    const updateImageSources = useCallback(async (html) => {
      if (!html) return "";

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));

      const imageUpdates = images.map(async (img) => {
        const fileName = img.getAttribute("src");

        // Preserve width and height
        if (img.getAttribute("width"))
          img.style.width = img.getAttribute("width");
        if (img.getAttribute("height"))
          img.style.height = img.getAttribute("height");

        // Preserve alignment attributes
        const dataAlign = img.getAttribute("data-text-align");
        if (dataAlign) {
          img.style.textAlign = dataAlign;
        }

        if (
          fileName &&
          !fileName.startsWith("http") &&
          !fileName.startsWith("data:")
        ) {
          try {
            const signedUrl = await generateSignedUrl(
              fileName,
              process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
            );
            img.src = signedUrl;
          } catch (error) {
            console.error("Error generating signed URL:", error);
          }
        }
      });

      await Promise.all(imageUpdates);
      return doc.body.innerHTML;
    }, []);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2]
          }
        }),
        Underline,
        LineHeight.configure({
          types: ["paragraph", "heading"],
          heights: ["1", "1.15", "1.5", "2", "2.5", "3"]
        }),
        TextAlign.configure({
          types: ["heading", "paragraph", "image"],
          alignments: ["left", "center", "right"]
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: "tltp"
          }
        }),
        ImageResize.configure({
          inline: false,
          allowBase64: true,
          HTMLAttributes: {
            class: "tiptap-image"
          }
        }),
        TextStyle,
        Color,
        FontFamily,
        FontSize
      ],
      content: "",
      onCreate: ({ editor }) => {
        // Editor is ready, initial content will be loaded via useEffect
        hasLoadedInitialContent.current = false;
      },
      onUpdate: ({ editor }) => {
        if (isUpdatingRef.current) return;

        const html = editor.getHTML();
        // Convert signed URLs back to filenames before saving
        const htmlWithFilenames = convertSignedUrlsToFilenames(html);
        setValue(htmlWithFilenames);
      },
      editorProps: {
        attributes: {
          class: "tiptap-editor"
        },
        handleKeyDown: (view, event) => {
          // Handle Tab key to prevent losing focus
          if (event.key === "Tab") {
            event.preventDefault();

            // Insert two non-breaking spaces as tab indentation
            const { state, dispatch } = view;
            const { tr } = state;
            tr.insertText("  "); // Insert 2 spaces
            dispatch(tr);

            return true; // Prevent default behavior
          }
          return false; // Allow other keys to work normally
        }
      }
    });

    // Expose the resetSelection method to parent components
    useImperativeHandle(ref, () => ({
      resetSelection: () => {
        if (editor) {
          editor.commands.blur();
          editor.commands.setTextSelection(0);
        }
      }
    }));

    // Load initial content when editor is ready
    useEffect(() => {
      if (!editor) return;

      // Load initial content only once when editor is first ready
      if (!hasLoadedInitialContent.current && value) {
        hasLoadedInitialContent.current = true;
        isUpdatingRef.current = true;

        updateImageSources(value).then((updatedHtml) => {
          editor.commands.setContent(updatedHtml, false);
          isUpdatingRef.current = false;
        });
      }
    }, [editor, value, updateImageSources]);

    // Update editor content when value prop changes externally (e.g., tab switching)
    useEffect(() => {
      if (!editor || !hasLoadedInitialContent.current) return;

      const currentContent = convertSignedUrlsToFilenames(editor.getHTML());

      // Check if value is different from current content
      if (value !== currentContent) {
        isUpdatingRef.current = true;
        updateImageSources(value || "").then((updatedHtml) => {
          editor.commands.setContent(updatedHtml, false);
          isUpdatingRef.current = false;
        });
      }
    }, [value, editor, updateImageSources, convertSignedUrlsToFilenames]);

    const handleOpenLinkDialog = () => {
      if (editor) {
        const previousUrl = editor.getAttributes("link").href || "";
        setCurrentLinkUrl(previousUrl);
        setLinkDialogOpen(true);
      }
    };

    const handleLinkSubmit = (url) => {
      if (editor) {
        if (url === "") {
          editor.chain().focus().extendMarkRange("link").unsetLink().run();
        } else {
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
        }
      }
      setLinkDialogOpen(false);
      setCurrentLinkUrl("");
    };

    const handleImageSubmit = (data, type) => {
      if (!editor) return;

      if (type === "file") {
        // Convert file to base64 and insert
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target.result;
          editor.chain().focus().setImage({ src: base64 }).run();
          setImageDialogOpen(false);
        };
        reader.readAsDataURL(data);
      }
    };

    if (!editor) {
      return null;
    }

    return (
      <>
        <Box
          className="tiptap-wrapper"
          sx={
            isLargeEditorNeeded && {
              "& .tiptap-editor-container": {
                height: "600px !important",
                maxHeight: "unset !important"
              }
            }
          }
        >
          <MenuBar
            editor={editor}
            onOpenLinkDialog={handleOpenLinkDialog}
            onOpenImageDialog={() => setImageDialogOpen(true)}
          />
          <Box className="tiptap-editor-container">
            <EditorContent editor={editor} ref={editorRef} />
          </Box>
        </Box>

        <LinkDialog
          open={linkDialogOpen}
          onClose={() => setLinkDialogOpen(false)}
          onSubmit={handleLinkSubmit}
          initialUrl={currentLinkUrl}
        />

        <ImageDialog
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          onSubmit={handleImageSubmit}
        />
      </>
    );
  }
);

export default RichTextEditor;
